/**
 * SB2-CC-01 — RoleContext provider for `apps/web`.
 *
 * Wraps `<RoleMatrixContextProvider>` (the read-side context primitive
 * in `@aeriosiq/ui`) and fills it with:
 *   - `role` — the caller's canonical role, sourced from the auth store
 *     (the JWT-resolved roles array; we collapse it to a single role
 *     per the `_BACKEND/.vibe/58_ROLE_MANAGEMENT.md` model). A user
 *     should have exactly one role at a time. Multiple roles are
 *     tolerated by picking the most-privileged via
 *     `pickPrimaryRole(roles)`.
 *   - `tenantSlug` — sourced from the same auth store.
 *   - `matrix` — fetched once per session via `useRoleMatrix` (TanStack
 *     query keyed on the JWT's `jti`).
 *   - `isLoading` — true until the first non-error fetch lands.
 *
 * Mount this provider at the umbrella shell level
 * (`apps/web/src/app/router.tsx`) inside the
 * `<RequireAuth><TenantGate>` subtree so the JWT + tenant are
 * available when `useRoleMatrix` fires.
 *
 * Why one provider in `apps/web` and the storage primitive in
 * `@aeriosiq/ui`?
 *   - `@aeriosiq/ui` deliberately has no auth / TanStack dep; if
 *     it did, every consumer module (`@aeriosiq/lyse`,
 *     `@aeriosiq/admin`) would inherit those deps.
 *   - The provider in `apps/web` injects the JWT, tenant slug, and
 *     query-client-driven data into the read-side context. Modules
 *     mounted under the shell `useRoleMatrixContext()` / the hooks
 *     transparently.
 */
import { useMemo, type ReactNode } from "react";
import { useAuthStore } from "./store.js";
import {
  RoleMatrixContextProvider,
  type Role,
  type RoleMatrixContextValue,
} from "@aeriosiq/ui";
import { useRoleMatrix } from "./useRoleMatrix.js";
// Story 074 — ONE canonical collapse of the transport `roles[]` array.
import { pickPrimaryRole } from "./pickPrimaryRole.js";

export interface RoleContextProviderProps {
  children: ReactNode;
}

export function RoleContextProvider({ children }: RoleContextProviderProps) {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);

  const role = useMemo<Role | null>(
    () => (session ? pickPrimaryRole(session.user.roles) : null),
    [session],
  );

  const { data: matrix, isLoading, isFetching } = useRoleMatrix();

  const value = useMemo<RoleMatrixContextValue>(
    () => ({
      role,
      tenantSlug: tenant?.tenantSlug ?? null,
      matrix: matrix ?? null,
      // While the very first fetch is in flight `isLoading` is true.
      // On a refetch (e.g. JWT rotation) we keep the previously-cached
      // matrix visible but expose `isFetching` so consumers can show a
      // subtle indicator if they want.
      isLoading: matrix ? false : isLoading || isFetching,
    }),
    [role, tenant?.tenantSlug, matrix, isLoading, isFetching],
  );

  return (
    <RoleMatrixContextProvider value={value}>
      {children}
    </RoleMatrixContextProvider>
  );
}
